import { CuriosityAutocomplete, CuriosityNamespace, CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { Operator } from '@procureai/query-builder';
import {
  ValueEditorDate,
  ValueEditorDateRange,
  ValueEditorInput,
  ValueEditorNumberInput,
  ValueEditorNumberRange,
  ValueEditorSelect,
} from '@procureai/query-builder-ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TsrctRuleListSourcingFilterField, TsrctRuleStatus, TsrctRuleVariant } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { AstronautUserAutocomplete } from '~/components/AstronautUserAutocomplete';
import {
  listOperators,
  operators,
  queryFieldFactory,
  UseDataGridQueryBuilderConfig,
} from '~/components/DataGridQueryBuilder';
import {
  ValueEditorSelectAutocomplete,
  ValueEditorSelectAutocompleteProps,
} from '~/components/ValueEditorSelectAutocomplete/ValueEditorSelectAutocomplete';

enum RuleQueryFieldGroup {
  RULE = 'rule',
  RESPONSIBLE = 'responsible',
}

const curiosityNsMap: { [key in TsrctRuleListSourcingFilterField]?: CuriosityNamespace } = {
  [TsrctRuleListSourcingFilterField.organizationIds]: CuriosityNamespaceType.ORGANIZATION,
  [TsrctRuleListSourcingFilterField.sourceSystemIds]: CuriosityNamespaceType.SOURCE_SYSTEM,
  [TsrctRuleListSourcingFilterField.commodityGroupIds]: CuriosityNamespaceType.COMMODITY_GROUP,
};

const enumAutocompleteMap: {
  [key in TsrctRuleListSourcingFilterField]?: Pick<
    ValueEditorSelectAutocompleteProps,
    'initialI18nPrefix' | 'initialOptions'
  >;
} = {
  [TsrctRuleListSourcingFilterField.status]: {
    initialI18nPrefix: 'RuleStatus',
    initialOptions: Object.keys(TsrctRuleStatus),
  },
  [TsrctRuleListSourcingFilterField.variant]: {
    initialI18nPrefix: 'RuleVariant',
    initialOptions: Object.keys(TsrctRuleVariant),
  },
};

export const useRuleListSourcingDataGridQueryBuilderConfig: UseDataGridQueryBuilderConfig = () => {
  const { t } = useTranslation(I18nNamespace.ruleBuilder);

  return useMemo(() => {
    const createField = queryFieldFactory(t, { i18nPrefix: 'RuleQueryBuilder' });
    return {
      fields: [
        // ---------- RULE ----------
        createField({
          name: TsrctRuleListSourcingFilterField.sequenceNumber,
          grp: RuleQueryFieldGroup.RULE,
          operators: operators.numNotEmpty,
        }),
        createField({
          name: TsrctRuleListSourcingFilterField.status,
          grp: RuleQueryFieldGroup.RULE,
          operators: operators.enumValue,
        }),
        createField({
          name: TsrctRuleListSourcingFilterField.variant,
          grp: RuleQueryFieldGroup.RULE,
          operators: operators.enumValue,
        }),
        createField({
          name: TsrctRuleListSourcingFilterField.updatedAt,
          grp: RuleQueryFieldGroup.RULE,
          operators: operators.date,
        }),
        createField({
          name: TsrctRuleListSourcingFilterField.createdAt,
          grp: RuleQueryFieldGroup.RULE,
          operators: operators.date,
        }),
        createField({
          name: TsrctRuleListSourcingFilterField.organizationIds,
          grp: RuleQueryFieldGroup.RULE,
          operators: operators.idAuto,
        }),
        createField({
          name: TsrctRuleListSourcingFilterField.sourceSystemIds,
          grp: RuleQueryFieldGroup.RULE,
          operators: operators.idAuto,
        }),
        createField({
          name: TsrctRuleListSourcingFilterField.commodityGroupIds,
          grp: RuleQueryFieldGroup.RULE,
          operators: operators.idAuto,
        }),
        // ---------- RESPONSIBLE ----------
        createField({
          name: TsrctRuleListSourcingFilterField.responsibleIds,
          grp: RuleQueryFieldGroup.RESPONSIBLE,
          operators: operators.idAuto,
        }),
      ],
      getOperatorLabel: ({ field, operator }) => {
        switch (field) {
          case TsrctRuleListSourcingFilterField.createdAt:
          case TsrctRuleListSourcingFilterField.updatedAt:
            if (operator === Operator.LT || operator === Operator.GT) {
              return `QueryBuilder.operatorDate.${operator}`;
            }
        }
      },
      renderValueEditor: ({ field, operator, ...otherProps }) => {
        if (operator === Operator.EMPTY || operator === Operator.NOT_EMPTY) {
          return null;
        }
        switch (field) {
          //
          case TsrctRuleListSourcingFilterField.name:
            if (listOperators[operator]) {
              return <ValueEditorSelect {...otherProps} multiple={true} freeSolo />;
            }
            return <ValueEditorInput {...otherProps} />;
          case TsrctRuleListSourcingFilterField.status: {
            const enumOptions = enumAutocompleteMap[field];
            if (enumOptions) {
              return (
                <ValueEditorSelectAutocomplete {...otherProps} {...enumOptions} multiple={listOperators[operator]} />
              );
            }
            return null;
          }
          case TsrctRuleListSourcingFilterField.variant: {
            const enumOptions = enumAutocompleteMap[field];
            if (enumOptions) {
              return (
                <ValueEditorSelectAutocomplete {...otherProps} {...enumOptions} multiple={listOperators[operator]} />
              );
            }
            return null;
          }
          case TsrctRuleListSourcingFilterField.sourceSystemIds:
          case TsrctRuleListSourcingFilterField.organizationIds:
          case TsrctRuleListSourcingFilterField.commodityGroupIds: {
            const ns = curiosityNsMap[field];
            if (ns) {
              return <CuriosityAutocomplete ns={ns} multiple={listOperators[operator]} simplifyValue {...otherProps} />;
            }
            return null;
          }
          case TsrctRuleListSourcingFilterField.responsibleIds:
            return (
              <AstronautUserAutocomplete
                multiple={listOperators[operator]}
                hasEmptyOption
                translationPrefix="ResponsibleUsersSelect.options"
                {...otherProps}
              />
            );
          case TsrctRuleListSourcingFilterField.sequenceNumber:
            if (operator === Operator.BETWEEN) {
              return <ValueEditorNumberRange {...otherProps} />;
            }
            return <ValueEditorNumberInput min={0} {...otherProps} />;
          case TsrctRuleListSourcingFilterField.updatedAt:
          case TsrctRuleListSourcingFilterField.createdAt:
            if (operator === Operator.BETWEEN) {
              return <ValueEditorDateRange {...otherProps} />;
            }
            return <ValueEditorDate {...otherProps} />;
          default:
            return null;
        }
      },
    };
  }, [t]);
};
